/**
 * Grid decomposition utility — port of the Python `decompose_into_grids()` logic
 * from `agentic_workflow/agents/storyboard_designer/agent.py`.
 *
 * Converts a total panel count into a sequence of grid sizes (3×3, 2×2, 1×1)
 * and provides position labels for each panel within each grid.
 */

/** Position labels for a 3×3 grid (9 panels). */
const GRID_LABELS_3x3: readonly string[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
]

/** Position labels for a 2×2 grid (4 panels). */
const GRID_LABELS_2x2: readonly string[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]

/** Position label for a 1×1 grid. */
const GRID_LABELS_1x1: readonly string[] = ['center']

/**
 * Map a `positionLabel` to its `canvas.json` i18n key.
 *
 * The label values above stay canonical English kebab-case: they are STABLE
 * IDS, and the backend mirrors the same vocabulary in `_9GRID_LABELS` /
 * `_4GRID_LABELS` (`storyboard_designer/agent.py`) where it is spliced into the
 * IMAGE prompt and must remain English. Only the console's rendering localizes.
 *
 * Unknown values (the `panel-N` fallback below) return `undefined`, and the
 * caller renders the raw string rather than a missing key.
 */
export function positionLabelKey(label: string): string | undefined {
  if (!/^[a-z]+(-[a-z]+)?$/.test(label)) return undefined
  return label.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
}

export type GridSize = [rows: number, cols: number]

export interface GridPanel {
  /** 1-based panel number across the entire storyboard. */
  panelNumber: number
  /** Position label within the grid (e.g. "top-left"). */
  positionLabel: string
  /** Beat description text, if available. */
  beat?: string
}

export interface GridSection {
  /** 0-based grid index. */
  gridIndex: number
  /** Grid dimensions [rows, cols]. */
  size: GridSize
  /** Panels in this grid. */
  panels: GridPanel[]
}

/**
 * Greedy decomposition of panel count into grid sizes.
 * Fills with the largest grid first: 9 → 3×3, 4 → 2×2, 1 → 1×1.
 */
export function decomposeIntoGrids(panelCount: number): GridSize[] {
  if (panelCount < 1) return []
  const grids: GridSize[] = []
  let remaining = panelCount
  while (remaining >= 9) {
    grids.push([3, 3])
    remaining -= 9
  }
  while (remaining >= 4) {
    grids.push([2, 2])
    remaining -= 4
  }
  while (remaining >= 1) {
    grids.push([1, 1])
    remaining -= 1
  }
  return grids
}

/** Returns position labels for a given grid size. */
function getGridLabels(rows: number, cols: number): readonly string[] {
  if (rows === 3 && cols === 3) return GRID_LABELS_3x3
  if (rows === 2 && cols === 2) return GRID_LABELS_2x2
  return GRID_LABELS_1x1
}

/**
 * Build structured grid sections from a panel count and optional beat descriptions.
 * Beats are sliced sequentially into grids, matching the Python agent's `beat_offset` logic.
 */
export function buildGridSections(
  panelCount: number,
  beats: string[] = [],
): GridSection[] {
  const grids = decomposeIntoGrids(panelCount)
  const sections: GridSection[] = []
  let beatOffset = 0

  for (let gridIdx = 0; gridIdx < grids.length; gridIdx++) {
    const [rows, cols] = grids[gridIdx]
    const total = rows * cols
    const labels = getGridLabels(rows, cols)
    const panels: GridPanel[] = []

    for (let i = 0; i < total; i++) {
      panels.push({
        panelNumber: beatOffset + i + 1,
        positionLabel: labels[i] ?? `panel-${i + 1}`,
        beat: beats[beatOffset + i],
      })
    }

    sections.push({ gridIndex: gridIdx, size: [rows, cols], panels })
    beatOffset += total
  }

  return sections
}
